// ============================================================================
//  SOFIA VOICE ANNOUNCER - Premium Table Only

// ============================================================================
// Uses pre-recorded Sofia MP3 files for high-quality seductive voice
// This is ONLY for Premium table - Classic tables use VoiceAnnouncer.ts
// ============================================================================

// Sofia audio file mappings based on actual files in /public/audio/sofia/
const SOFIA_AUDIO: Record<string, string[]> = {
  // Actions
  fold: [
    '/audio/sofia/sofia_fold1.mp3',
    '/audio/sofia/sofia_fold2.mp3',
    '/audio/sofia/sofia_fold3.mp3',
  ],
  check: [
    '/audio/sofia/sofia_check1.mp3',
    '/audio/sofia/sofia_check2.mp3',
    '/audio/sofia/sofia_check3.mp3',
  ],
  call: [
    '/audio/sofia/sofia_call1.mp3',
    '/audio/sofia/sofia_call2.mp3',
    '/audio/sofia/sofia_call3.mp3',
  ],
  raise: [
    '/audio/sofia/sofia_raise1.mp3',
    '/audio/sofia/sofia_raise2.mp3',
    '/audio/sofia/sofia_raise3.mp3',
  ],
  allin: [
    '/audio/sofia/sofia_allin1.mp3',
    '/audio/sofia/sofia_allin2.mp3',
    '/audio/sofia/sofia_allin3.mp3',
  ],
  
  // Community cards
  flop: [
    '/audio/sofia/sofia_flop1.mp3',
    '/audio/sofia/sofia_flop2.mp3',
    '/audio/sofia/sofia_flop3.mp3',
  ],
  turn: [
    '/audio/sofia/sofia_turn1.mp3',
    '/audio/sofia/sofia_turn2.mp3',
  ],
  river: [
    '/audio/sofia/sofia_river1.mp3',
    '/audio/sofia/sofia_river2.mp3',
  ],
  showdown: [
    '/audio/sofia/sofia_showdown1.mp3',
    '/audio/sofia/sofia_showdown2.mp3',
    '/audio/sofia/sofia_showdown3.mp3',
  ],
  
  // Turn notifications
  yourturn: [
    '/audio/sofia/sofia_yourturn1.mp3',
    '/audio/sofia/sofia_yourturn2.mp3',
    '/audio/sofia/sofai_yourturn3.mp3', // Note: typo in actual filename
  ],
  
  // Winner/Loser
  winner: [
    '/audio/sofia/sofia_winner1.mp3',
    '/audio/sofia/sofia_win2.mp3',
    '/audio/sofia/sofia_win3.mp3',
  ],
  lose: [
    '/audio/sofia/sofia_lose1.mp3',
    '/audio/sofia/sofia_lose2.mp3',
  ],
  splitpot: [
    '/audio/sofia/sofia_splitpot.mp3',
  ],
  
  // Game flow
  gamestart: [
    '/audio/sofia/sofia_gamestart.mp3',
  ],
  newhand: [
    '/audio/sofia/sofia_newhand1.mp3',
    '/audio/sofia/sofia_newhand2.mp3',
  ],
  bigpot: [
    '/audio/sofia/sofia_bigpot1.mp3',
    '/audio/sofia/sofia_bigpot2.mp3',
  ],
  
  // Dealer tips
  tip: [
    '/audio/sofia/sofia_dealertip1.mp3',
    '/audio/sofia/sofia_dealtip2.mp3',
  ],
};

class SofiaVoiceAnnouncer {
  private enabled: boolean = true;
  private volume: number = 0.85;
  private currentAudio: HTMLAudioElement | null = null;
  private lastAnnouncementTime: number = 0;
  private minTimeBetweenAnnouncements: number = 1200; // Prevent overlap
  private preloadedAudio: Map<string, HTMLAudioElement> = new Map();

  constructor() {
    this.preloadAudio();
  }

  private preloadAudio(): void {
    if (typeof window === 'undefined') return;
    
    // Preload most common sounds for instant playback
    const priorityFiles = [
      ...SOFIA_AUDIO.yourturn,
      ...SOFIA_AUDIO.flop,
      ...SOFIA_AUDIO.turn,
      ...SOFIA_AUDIO.river,
      ...SOFIA_AUDIO.winner,
      ...SOFIA_AUDIO.fold,
      ...SOFIA_AUDIO.check,
      ...SOFIA_AUDIO.call,
    ];
    
    priorityFiles.forEach(src => {
      try {
        const audio = new Audio(src);
        audio.preload = 'auto';
        audio.volume = this.volume;
        this.preloadedAudio.set(src, audio);
      } catch (e) {
        console.warn(`Sofia: Failed to preload ${src}`);
      }
    });
  }

  private getRandomFile(category: string): string | null {
    const files = SOFIA_AUDIO[category];
    if (!files || files.length === 0) return null;
    return files[Math.floor(Math.random() * files.length)];
  }

  private playAudio(category: string): void {
    if (!this.enabled) return;
    
    // Prevent rapid-fire announcements
    const now = Date.now();
    if (now - this.lastAnnouncementTime < this.minTimeBetweenAnnouncements) {
      return;
    }
    this.lastAnnouncementTime = now;

    const src = this.getRandomFile(category);
    if (!src) return;

    try {
      // Stop current audio if playing
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
      }

      // Use preloaded audio if available, otherwise create new
      let audio = this.preloadedAudio.get(src);
      if (!audio) {
        audio = new Audio(src);
      }
      
      audio.volume = this.volume;
      audio.currentTime = 0;
      this.currentAudio = audio;
      
      audio.play().catch(err => {
        console.warn(`Sofia audio failed: ${src}`, err);
      });
    } catch (err) {
      console.warn(`Sofia audio error: ${category}`, err);
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.stop();
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    // Update preloaded audio volumes
    this.preloadedAudio.forEach(audio => {
      audio.volume = this.volume;
    });
  }

  public stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }
  }

  public isSpeaking(): boolean {
    return this.currentAudio ? !this.currentAudio.paused : false;
  }

  // No-ops for compatibility with VoiceAnnouncer API
  public setVoiceGender(_gender: 'male' | 'female'): void {}
  public setVoiceFromDealerImage(_url: string): void {}

  // ============================================================================
  // ACTION ANNOUNCEMENTS
  // ============================================================================

  public announceFold(_playerName?: string): void {
    this.playAudio('fold');
  }

  public announceCheck(_playerName?: string): void {
    this.playAudio('check');
  }

  public announceCall(_playerName?: string): void {
    this.playAudio('call');
  }

  public announceRaise(_playerName?: string, _amount?: number): void {
    this.playAudio('raise');
  }

  public announceAllIn(_playerName?: string): void {
    this.playAudio('allin');
  }

  // ============================================================================
  // COMMUNITY CARDS
  // ============================================================================

  public announceFlop(): void {
    this.playAudio('flop');
  }

  public announceTurn(): void {
    this.playAudio('turn');
  }

  public announceRiver(): void {
    this.playAudio('river');
  }

  public announceShowdown(): void {
    this.playAudio('showdown');
  }

  // ============================================================================
  // GAME FLOW
  // ============================================================================

  public announceYourTurn(): void {
    this.playAudio('yourturn');
  }

  public announceWinner(_playerName?: string, _isYou?: boolean, _handDescription?: string, _isFoldWin?: boolean): void {
    this.playAudio('winner');
  }

  public announceLoss(): void {
    this.playAudio('lose');
  }

  public announceSplitPot(_isYou?: boolean, _winnerNames?: string[]): void {
    this.playAudio('splitpot');
  }

  public announceGameStart(): void {
    this.playAudio('gamestart');
  }

  public announceNewHand(_handNumber?: number): void {
    this.playAudio('newhand');
  }

  public announceBigPot(_potSize?: number): void {
    this.playAudio('bigpot');
  }

  // ============================================================================
  // TIPS & SPECIAL
  // ============================================================================

  public announceDealerTip(_tipperName?: string, _amount?: number, _currency?: string, _isYou?: boolean): void {
    this.playAudio('tip');
  }

  public announceTipThankYou(): void {
    this.playAudio('tip');
  }

  public announceBombPot(_ante?: number): void {
    this.playAudio('bigpot');
  }

  public announce72Bounty(_winnerName?: string, _bountyAmount?: number): void {
    this.playAudio('winner');
  }

  public announceStraddle(_playerName?: string, _amount?: number): void {
    this.playAudio('raise');
  }

  public announceRunItTwice(): void {
    this.playAudio('showdown');
  }

  // ============================================================================
  // GENERIC
  // ============================================================================

  public say(_text: string, _type?: string): void {
    this.playAudio('newhand');
  }

  public announce(text: string): void {
    const lower = text.toLowerCase();
    if (lower.includes('bomb')) this.playAudio('bigpot');
    else if (lower.includes('win')) this.playAudio('winner');
    else this.playAudio('newhand');
  }

  // No-ops for compatibility
  public announcePlayerJoined(_name: string): void {}
  public announcePlayerLeft(_name: string): void {}
  public announceBlinds(_small: number, _big: number): void {}
}

// Export singleton instance
const sofiaVoiceAnnouncer = new SofiaVoiceAnnouncer();
export default sofiaVoiceAnnouncer;